import {Component, Input, OnChanges, output, SimpleChanges, ViewChild} from '@angular/core';
import {
  MatNestedTreeNode,
  MatTree,
  MatTreeNestedDataSource,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodeOutlet,
  MatTreeNodeToggle
} from '@angular/material/tree';
import { RouteConfiguration, RouteHierarchyItem } from '../../../../../shared/models/route';
import {SelectionModel} from '@angular/cdk/collections';
import {MatIcon} from '@angular/material/icon';
import {MatCheckbox} from '@angular/material/checkbox';
import { MatIconButton } from '@angular/material/button';
import { LineStyleEditorComponent } from '../../../../../shared/components/line-style-editor/line-style-editor.component';
import { LineStyle } from '../../../../../shared/models/styling';

export interface RouteFilter {
  routeIds: number[];
  all: boolean;
}

@Component({
    selector: 'app-route-tree',
    templateUrl: './route-tree.component.html',
  imports: [
    MatTree,
    MatIcon,
    MatCheckbox,
    MatTreeNode,
    MatNestedTreeNode,
    MatTreeNodeDef,
    MatTreeNodeToggle,
    MatIconButton,
    MatTreeNodeOutlet,
    LineStyleEditorComponent
  ],
    styleUrls: ['./route-tree.component.scss']
})
export class RouteTreeComponent implements OnChanges {

  @Input() routeConfiguration: RouteConfiguration;
  @Input() attributes: string[];
  @Input() filteringMode = true;
  @Input() readonlyPreviewMode = false;
  @Input() defaultStyle: LineStyle;

  routeFiltered = output<RouteFilter>();
  styleChanged = output<RouteConfiguration>();

  routeHover = output<RouteHierarchyItem>();
  routeLeave = output<RouteHierarchyItem>();

  @ViewChild('treeControl') tree: MatTree<RouteHierarchyItem>;

  dataSource: MatTreeNestedDataSource<RouteHierarchyItem> = new MatTreeNestedDataSource<RouteHierarchyItem>();

  checklistSelection = new SelectionModel<RouteHierarchyItem>(true);

  childrenAccessor = (node: RouteHierarchyItem) => node.children ?? [];

  constructor() { }

  ngOnChanges(_: SimpleChanges) {
    if (!this.routeConfiguration || !this.attributes) {
      return;
    }

    if (this.filteringMode) {
      this.childrenAccessor = (node: RouteHierarchyItem) => node.children ?? [];
      this.dataSource.data = this.routeConfiguration.children;
    } else {
      const filterOutHiddenNodes = (node: RouteHierarchyItem) => !node.hidden;
      this.childrenAccessor = (node: RouteHierarchyItem) => node.children?.filter(filterOutHiddenNodes) || [];
      this.dataSource.data = this.routeConfiguration.children.filter(filterOutHiddenNodes);
    }

    const allItems = RouteHierarchyItem.getFlat(this.attributes.length + 1, this.routeConfiguration.children);
    const visibleItems = allItems.filter(it => !it.hidden);
    if (allItems.length === visibleItems.length) {
      visibleItems.push(this.dataSource.data[0]);
    }

    // filter leaf level items and select by default
    this.checklistSelection = new SelectionModel<RouteHierarchyItem>(
        true,
        visibleItems
    );
    this.nodeSelectionChanged();

    // this.treeControl.dataNodes = this.dataSource.data;
    setTimeout(() => {
      this.tree.expandAll();
    }, 100);
  }

  hasChild = (_: number, node: RouteHierarchyItem) => !!node.children && node.children.length > 0;

  getLevel(node: RouteHierarchyItem) {
    if (!node.childrenAttribute) {
      return this.attributes.length;
    }
    return this.attributes.indexOf(node.childrenAttribute);
  }
  /*selectAll(event: MatCheckboxChange) {
    if (event.checked) {
      this.checklistSelection.select(...RouteHierarchyItem.getLeaves(this.attributes.length, this.routeConfiguration.children));
      this.changeMapLayerFilter([], true);
    } else {
      this.checklistSelection.clear();
      this.changeMapLayerFilter([], false);
    }
  }*/

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: RouteHierarchyItem): boolean {
    const descendants = this.childrenAccessor(node);
    return descendants.length > 0 &&
        descendants.every(child => {
          return this.checklistSelection.isSelected(child);
        });
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: RouteHierarchyItem): boolean {
    const descendants = this.childrenAccessor(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the item selection. Select/deselect all the descendants node */
  itemSelectionToggle(node: RouteHierarchyItem): void {
    this.checklistSelection.toggle(node);
    const descendants = this.childrenAccessor(node);
    const isNodeSelected = this.checklistSelection.isSelected(node);
    node.hidden = !isNodeSelected;
    isNodeSelected
        ? this.checklistSelection.select(...descendants)
        : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach(child => {
      this.checklistSelection.isSelected(child);
      child.hidden = !isNodeSelected;
    });
    this.checkAllParentsSelection(node);

    this.nodeSelectionChanged();
  }

  /** Toggle a leaf item selection. Check all the parents to see if they changed */
  leafItemSelectionToggle(node: RouteHierarchyItem): void {
    this.checklistSelection.toggle(node);
    const isNodeSelected = this.checklistSelection.isSelected(node);
    node.hidden = !isNodeSelected;
    this.checkAllParentsSelection(node);

    this.nodeSelectionChanged();
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: RouteHierarchyItem): void {
    let parent: RouteHierarchyItem | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: RouteHierarchyItem): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.childrenAccessor(node);
    const descAllSelected =
        descendants.length > 0 &&
        descendants.every(child => {
          return this.checklistSelection.isSelected(child);
        });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: RouteHierarchyItem): RouteHierarchyItem | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.routeConfiguration.children.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.routeConfiguration.children[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  nodeSelectionChanged() {
    const routeIds = this.checklistSelection.selected.map(node => node.routeId).filter(routeId => !!routeId);
    this.changeMapLayerFilter(routeIds);
  }

  changeMapLayerFilter(routeIds: number[], all: boolean = false) {
    this.routeFiltered.emit({routeIds, all});
  }

  styleOverrideUpdated(item: RouteHierarchyItem, $event: LineStyle) {
    item.styleOverride = $event;
    this.styleChanged.emit(this.routeConfiguration);
  }

  onHoverNode(node: RouteHierarchyItem) {
    this.routeHover.emit(node);
  }

  onLeaveNode(node: RouteHierarchyItem) {
    this.routeLeave.emit(node);
  }
}
